import { useRef, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { fetchLifelines, type LifelinePerson, type LifelinesData } from '@/api/lifelines.api';

// ─── Layout-Konstanten ───────────────────────────────────────────────────────

const NAME_COL_W = 196;   // Breite der Namensspalte (px)
const ROW_H      = 34;    // Höhe einer Personenzeile (px)
const AXIS_H     = 60;    // Höhe der Jahres-Achse (px) – oben Epoche-Labels, unten Ticks
const BAR_H      = 18;    // Höhe des Lebensbalkens (px)
const BAR_RADIUS = 4;     // Abrundung der Balken
const TICK_EVERY = 10;    // Jahresbeschriftung alle N Jahre

// ─── Farben ──────────────────────────────────────────────────────────────────

const GENDER_COLOR: Record<string, string> = {
  male:    '#3b82f6',
  female:  '#ec4899',
  other:   '#8b5cf6',
  unknown: '#94a3b8',
};

function genderColor(gender: string | null): string {
  return GENDER_COLOR[gender ?? 'unknown'] ?? GENDER_COLOR.unknown;
}

// ─── Historische Epochen-Overlays ────────────────────────────────────────────

interface HistoricalOverlay {
  label: string;
  start: number;
  end: number;
  color: string;
  opacity: number;
}

const OVERLAYS: HistoricalOverlay[] = [
  { label: 'Dreißigjähriger Krieg',      start: 1618, end: 1648, color: '#7f1d1d', opacity: 0.10 },
  { label: 'Frz. Revolution',            start: 1789, end: 1799, color: '#1e3a8a', opacity: 0.10 },
  { label: 'Napoleonische Kriege',       start: 1799, end: 1815, color: '#1d4ed8', opacity: 0.10 },
  { label: 'Industrialisierung',         start: 1840, end: 1914, color: '#92400e', opacity: 0.06 },
  { label: 'Kaiserreich',                start: 1871, end: 1918, color: '#4c1d95', opacity: 0.07 },
  { label: '1. Weltkrieg',               start: 1914, end: 1918, color: '#dc2626', opacity: 0.18 },
  { label: 'Weimarer Republik',          start: 1919, end: 1933, color: '#0369a1', opacity: 0.08 },
  { label: 'NS-Zeit',                    start: 1933, end: 1945, color: '#7f1d1d', opacity: 0.14 },
  { label: '2. Weltkrieg',               start: 1939, end: 1945, color: '#b91c1c', opacity: 0.18 },
  { label: 'BRD / DDR',                 start: 1949, end: 1990, color: '#065f46', opacity: 0.05 },
  { label: 'Wirtschaftswunder',          start: 1955, end: 1973, color: '#92400e', opacity: 0.07 },
  { label: 'Deutsche Wiedervereinigung', start: 1989, end: 1991, color: '#4c1d95', opacity: 0.14 },
  { label: 'Digitales Zeitalter',        start: 1995, end: 2030, color: '#0f766e', opacity: 0.05 },
];

// ─── Tooltip ─────────────────────────────────────────────────────────────────

interface TooltipData {
  x: number;
  y: number;
  lines: string[];
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function personLabel(p: LifelinePerson): string {
  return [p.firstName, p.lastName].filter(Boolean).join(' ');
}

function lifespan(p: LifelinePerson): string {
  const birth = p.birthYear ?? '?';
  const death = p.deathYear ?? (p.fateUnknown ? '†?' : 'heute');
  return `${birth} – ${death}`;
}

function overlaps(
  p: LifelinePerson,
  sel: LifelinePerson,
  currentYear: number,
): boolean {
  // endYear kommt vom Backend (Tod / heute / letztes bekanntes Ereignis)
  const pEnd   = p.endYear   ?? p.deathYear   ?? currentYear;
  const selEnd = sel.endYear ?? sel.deathYear ?? currentYear;
  const pStart   = p.birthYear   ?? 0;
  const selStart = sel.birthYear ?? 0;
  return pStart <= selEnd && pEnd >= selStart;
}

// ─── Achsen-Beschriftung ─────────────────────────────────────────────────────

function YearAxis({
  yearMin, yearMax, pxPerYear, width, overlays, showOverlays,
}: {
  yearMin: number; yearMax: number; pxPerYear: number; width: number;
  overlays: HistoricalOverlay[]; showOverlays: boolean;
}) {
  const ticks: number[] = [];
  const firstTick = Math.ceil(yearMin / TICK_EVERY) * TICK_EVERY;
  for (let y = firstTick; y <= yearMax; y += TICK_EVERY) {
    ticks.push(y);
  }

  // Oberer Bereich: Epoche-Labels (y 0–22), Trennlinie bei y=22
  // Unterer Bereich: Jahres-Ticks (y 22–AXIS_H)
  const LABEL_ZONE = 22;

  return (
    <svg width={width} height={AXIS_H} style={{ display: 'block' }}>
      {/* ── Epoche-Labels (obere Zone) ──────────────────────────────── */}
      {showOverlays && overlays.map((o) => {
        const x1 = Math.max(0, (o.start - yearMin) * pxPerYear);
        const x2 = Math.min(width, (o.end - yearMin) * pxPerYear);
        const w  = x2 - x1;
        if (w < 8) return null;
        return (
          <g key={o.label}>
            {/* Farbiger Hintergrundstreifen */}
            <rect x={x1} y={0} width={w} height={LABEL_ZONE - 2} fill={o.color} opacity={o.opacity * 2.5 + 0.05} />
            {/* Epoche-Name, nur wenn genug Platz */}
            {w > 30 && (
              <text
                x={x1 + w / 2}
                y={13}
                textAnchor="middle"
                fontSize={7}
                fontWeight="600"
                fill={o.color}
                opacity={0.9}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {w > 60 ? o.label : o.label.split(' ')[0]}
              </text>
            )}
          </g>
        );
      })}

      {/* Trennlinie zwischen Epoche-Zone und Tick-Zone */}
      <line x1={0} y1={LABEL_ZONE} x2={width} y2={LABEL_ZONE} stroke="#e2e8f0" strokeWidth={1} />

      {/* ── Jahres-Ticks (untere Zone) ───────────────────────────────── */}
      {/* Hintergrundlinie unten */}
      <line x1={0} y1={AXIS_H - 1} x2={width} y2={AXIS_H - 1} stroke="#e2e8f0" />
      {ticks.map((year) => {
        const x = (year - yearMin) * pxPerYear;
        const isMajor = year % 50 === 0;
        return (
          <g key={year}>
            <line
              x1={x} y1={isMajor ? AXIS_H - 16 : AXIS_H - 8}
              x2={x} y2={AXIS_H - 1}
              stroke={isMajor ? '#94a3b8' : '#cbd5e1'}
              strokeWidth={isMajor ? 1.5 : 1}
            />
            <text
              x={x}
              y={LABEL_ZONE + (isMajor ? 14 : 18)}
              textAnchor="middle"
              fontSize={isMajor ? 11 : 9}
              fontWeight={isMajor ? '600' : '400'}
              fill={isMajor ? '#475569' : '#94a3b8'}
            >
              {year}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Haupt-SVG: Balken + Overlays + Meilensteine ─────────────────────────────

function TimelineSVG({
  persons,
  yearMin,
  yearMax,
  pxPerYear,
  showOverlays,
  selectedIds,
  hoveredId,
  currentYear,
  onPersonClick,
  onMouseMove,
  onMouseLeave,
}: {
  persons: LifelinePerson[];
  yearMin: number;
  yearMax: number;
  pxPerYear: number;
  showOverlays: boolean;
  selectedIds: Set<string>;
  hoveredId: string | null;
  currentYear: number;
  onPersonClick: (p: LifelinePerson) => void;
  onMouseMove: (e: React.MouseEvent<SVGElement>, lines: string[]) => void;
  onMouseLeave: () => void;
}) {
  const totalW = (yearMax - yearMin) * pxPerYear;
  const totalH = persons.length * ROW_H;
  const barY   = (ROW_H - BAR_H) / 2;

  const xOf = (year: number) => (year - yearMin) * pxPerYear;

  // Alle ausgewählten Personen für den Zeitzeugen-Filter
  const selectedPersons = persons.filter((p) => selectedIds.has(p.id));

  return (
    <svg
      width={totalW}
      height={totalH}
      style={{ display: 'block', cursor: 'default' }}
    >
      {/* ── Historische Overlays ─────────────────────────────────────────── */}
      {showOverlays && OVERLAYS.map((o) => {
        const x1 = Math.max(0, xOf(o.start));
        const x2 = Math.min(totalW, xOf(o.end));
        if (x2 <= x1) return null;
        return (
          <rect
            key={o.label}
            x={x1} y={0}
            width={x2 - x1} height={totalH}
            fill={o.color}
            opacity={o.opacity}
          />
        );
      })}

      {/* ── Zebrastreifen für Lesbarkeit ─────────────────────────────────── */}
      {persons.map((_, i) => i % 2 === 1 && (
        <rect
          key={i}
          x={0} y={i * ROW_H}
          width={totalW} height={ROW_H}
          fill="#f8fafc"
          opacity={0.5}
        />
      ))}

      {/* ── Vertikale Gitterlinien ────────────────────────────────────────── */}
      {(() => {
        const lines: number[] = [];
        const first = Math.ceil(yearMin / TICK_EVERY) * TICK_EVERY;
        for (let y = first; y <= yearMax; y += TICK_EVERY) lines.push(y);
        return lines.map((year) => (
          <line
            key={year}
            x1={xOf(year)} y1={0}
            x2={xOf(year)} y2={totalH}
            stroke="#e2e8f0"
            strokeWidth={year % 50 === 0 ? 1 : 0.5}
            opacity={year % 50 === 0 ? 0.8 : 0.5}
          />
        ));
      })()}

      {/* ── Personenbalken ────────────────────────────────────────────────── */}
      {persons.map((p, i) => {
        if (p.birthYear === null) return null;

        const birth    = p.birthYear;
        // Balkenende vom Backend: Tod / heute (plausibel lebend) / letztes
        // bekanntes Ereignis (Schicksal unbekannt) – nie stumpf bis heute.
        const death    = p.endYear ?? p.deathYear ?? birth;
        const barX     = xOf(birth);
        const barW     = Math.max(2, xOf(death) - xOf(birth));
        const rowY     = i * ROW_H;
        const color    = genderColor(p.gender);
        const isSelected = selectedIds.has(p.id);
        const isHovered  = p.id === hoveredId;

        // Zeitzeugen-Filter: Personen die mit keiner ausgewählten Person überlappt → ausgegraut
        let opacity = 1;
        if (selectedPersons.length > 0 && !isSelected) {
          const overlapsAny = selectedPersons.some((sel) => overlaps(p, sel, currentYear));
          opacity = overlapsAny ? 0.85 : 0.12;
        }

        return (
          <g
            key={p.id}
            opacity={opacity}
            style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
            onClick={() => onPersonClick(p)}
          >
            {/* Hover-Highlight-Zeile */}
            {isHovered && (
              <rect
                x={0} y={rowY}
                width={totalW} height={ROW_H}
                fill={color}
                opacity={0.06}
              />
            )}

            {/* Lebensbalken */}
            <rect
              x={barX}
              y={rowY + barY}
              width={barW}
              height={BAR_H}
              rx={BAR_RADIUS}
              fill={color}
              opacity={isSelected ? 1 : isHovered ? 0.85 : 0.7}
            />

            {/* Auswahlrahmen */}
            {isSelected && (
              <rect
                x={barX - 1}
                y={rowY + barY - 1}
                width={barW + 2}
                height={BAR_H + 2}
                rx={BAR_RADIUS + 1}
                fill="none"
                stroke={color}
                strokeWidth={2}
              />
            )}

            {/* Schicksal unbekannt: kurzer gestrichelter Auslauf hinter dem
                letzten bekannten Ereignis statt einer Linie bis heute */}
            {p.fateUnknown && (
              <line
                x1={barX + barW}
                y1={rowY + barY + BAR_H / 2}
                x2={barX + barW + 18}
                y2={rowY + barY + BAR_H / 2}
                stroke={color}
                strokeWidth={2}
                strokeDasharray="4 3"
                opacity={0.5}
              />
            )}

            {/* "lebend"-Indikator: Pfeil am rechten Ende (nur plausibel Lebende) */}
            {p.isLiving && !p.fateUnknown && (
              <polygon
                points={`
                  ${barX + barW},${rowY + barY + BAR_H / 2 - 4}
                  ${barX + barW + 7},${rowY + barY + BAR_H / 2}
                  ${barX + barW},${rowY + barY + BAR_H / 2 + 4}
                `}
                fill={color}
                opacity={0.8}
              />
            )}

            {/* ── Meilensteine ───────────────────────────────────────────── */}
            {p.milestones.map((ms, mi) => {
              const mx = xOf(ms.year);
              if (mx < barX || mx > barX + barW) return null; // außerhalb des Balkens

              if (ms.type === 'marriage') {
                // Hochzeitsring: kleiner weißer Kreis mit Rahmen
                return (
                  <g
                    key={mi}
                    onMouseMove={(e) => onMouseMove(e, [`💍 Hochzeit ${ms.year}`, ms.label])}
                    onMouseLeave={onMouseLeave}
                    style={{ cursor: 'default' }}
                  >
                    <circle
                      cx={mx}
                      cy={rowY + barY + BAR_H / 2}
                      r={5}
                      fill="white"
                      stroke={color}
                      strokeWidth={1.5}
                    />
                    <circle
                      cx={mx}
                      cy={rowY + barY + BAR_H / 2}
                      r={2.5}
                      fill={color}
                      opacity={0.6}
                    />
                  </g>
                );
              } else {
                // Geburt eines Kindes: kleiner goldener Punkt
                return (
                  <circle
                    key={mi}
                    cx={mx}
                    cy={rowY + barY + BAR_H - 3}
                    r={2.5}
                    fill="#f59e0b"
                    stroke="white"
                    strokeWidth={0.8}
                    onMouseMove={(e) => onMouseMove(e, [`👶 Kind geboren ${ms.year}`, ms.label])}
                    onMouseLeave={onMouseLeave}
                    style={{ cursor: 'default' }}
                  />
                );
              }
            })}
          </g>
        );
      })}

      {/* ── "Heute"-Linie ────────────────────────────────────────────────── */}
      {currentYear >= yearMin && currentYear <= yearMax && (
        <g>
          <line
            x1={xOf(currentYear)} y1={0}
            x2={xOf(currentYear)} y2={totalH}
            stroke="#dc2626"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            opacity={0.6}
          />
          <text
            x={xOf(currentYear) + 3}
            y={12}
            fontSize={8}
            fill="#dc2626"
            fontWeight="600"
            opacity={0.8}
          >
            heute
          </text>
        </g>
      )}
    </svg>
  );
}

// ─── Namensspalte ─────────────────────────────────────────────────────────────

function NameColumn({
  persons,
  selectedIds,
  hoveredId,
  selectedPersons,
  currentYear,
  onToggle,
  onHover,
}: {
  persons: LifelinePerson[];
  selectedIds: Set<string>;
  hoveredId: string | null;
  selectedPersons: LifelinePerson[];
  currentYear: number;
  onToggle: (p: LifelinePerson) => void;
  onHover: (id: string | null) => void;
}) {
  return (
    <div style={{ width: `clamp(120px, 45vw, ${NAME_COL_W}px)` }}>
      {persons.map((p) => {
        const isSelected = selectedIds.has(p.id);
        const isHovered  = p.id === hoveredId;
        const color      = genderColor(p.gender);

        // Zeitzeugen-Filterung
        let faded = false;
        if (selectedPersons.length > 0 && !isSelected) {
          faded = !selectedPersons.some((sel) => overlaps(p, sel, currentYear));
        }

        return (
          <div
            key={p.id}
            style={{
              height: ROW_H,
              opacity: faded ? 0.28 : 1,
              transition: 'opacity 0.2s',
              borderLeft: isSelected ? `3px solid ${color}` : '3px solid transparent',
              backgroundColor: isSelected
                ? `${color}18`
                : isHovered
                  ? '#f8fafc'
                  : 'transparent',
            }}
            className="flex cursor-pointer items-center gap-1.5 border-b border-slate-100 px-2"
            onClick={() => onToggle(p)}
            onMouseEnter={() => onHover(p.id)}
            onMouseLeave={() => onHover(null)}
          >
            {/* Checkbox */}
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggle(p)}
              onClick={(e) => e.stopPropagation()}
              className="h-3 w-3 flex-shrink-0 cursor-pointer rounded accent-blue-500"
            />
            {/* Name */}
            <span
              className="truncate text-xs font-medium text-slate-700"
              title={personLabel(p)}
            >
              {personLabel(p)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Zeitzeugen-Infobox ──────────────────────────────────────────────────────

function ZeitzeugenInfo({
  selectedPersons,
  allPersons,
  currentYear,
  onClose,
  onRemove,
}: {
  selectedPersons: LifelinePerson[];
  allPersons: LifelinePerson[];
  currentYear: number;
  onClose: () => void;
  onRemove: (id: string) => void;
}) {
  // Personen, die mit ALLEN ausgewählten Personen gleichzeitig gelebt haben
  const commonContemporaries = allPersons.filter(
    (p) =>
      !selectedPersons.some((sel) => sel.id === p.id) &&
      selectedPersons.every((sel) => overlaps(p, sel, currentYear)),
  );

  // Personen, die mit MINDESTENS EINER ausgewählten Person gleichzeitig gelebt haben
  const anyContemporaries = allPersons.filter(
    (p) =>
      !selectedPersons.some((sel) => sel.id === p.id) &&
      selectedPersons.some((sel) => overlaps(p, sel, currentYear)),
  );

  const showCommon = selectedPersons.length > 1;

  return (
    <div className="flex-shrink-0 w-52 border-l border-slate-200 bg-white p-3 text-xs overflow-y-auto sm:w-64">
      {/* Header */}
      <div className="mb-2 flex items-start justify-between gap-1">
        <p className="font-semibold text-slate-700">
          {selectedPersons.length === 1 ? 'Ausgewählt' : `${selectedPersons.length} Personen`}
        </p>
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          title="Auswahl aufheben"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Liste der ausgewählten Personen */}
      <div className="mb-3 space-y-1.5">
        {selectedPersons.map((sel) => (
          <div key={sel.id} className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5">
            <div className="flex items-start justify-between gap-1">
              <div>
                <p className="font-semibold text-slate-800 leading-tight">{personLabel(sel)}</p>
                <p className="text-slate-500">{lifespan(sel)}</p>
              </div>
              <button
                onClick={() => onRemove(sel.id)}
                className="flex-shrink-0 mt-0.5 rounded p-0.5 text-slate-300 hover:bg-slate-200 hover:text-slate-500"
                title="Auswahl entfernen"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Meilensteine der Person */}
            {sel.milestones.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {sel.milestones.map((ms, i) => (
                  <li key={i} className="flex items-center gap-1 text-slate-500">
                    <span>{ms.type === 'marriage' ? '💍' : '👶'}</span>
                    <span>{ms.year} · {ms.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Gemeinsame Zeitzeugen (nur bei Mehrfachauswahl) */}
      {showCommon && (
        <div className="mb-2 rounded bg-indigo-50 px-2 py-1.5 text-indigo-700">
          <strong>{commonContemporaries.length}</strong> Person{commonContemporaries.length !== 1 ? 'en' : ''} lebten mit <em>allen</em> gleichzeitig
        </div>
      )}

      {/* Zeitzeugenliste */}
      {!showCommon && (
        <div className="mb-2 rounded bg-blue-50 px-2 py-1.5 text-blue-700">
          <strong>{anyContemporaries.length}</strong> Zeitzeug{anyContemporaries.length !== 1 ? 'en' : 'e'} lebten gleichzeitig
        </div>
      )}

      <div>
        <p className="mb-1 font-semibold text-slate-600">
          {showCommon ? 'Gemeinsame Zeitzeugen' : 'Gleichzeitig lebende Personen'}
        </p>
        {(showCommon ? commonContemporaries : anyContemporaries).length === 0 ? (
          <p className="text-slate-400 italic">Keine gefunden.</p>
        ) : (
          <ul className="space-y-0.5 text-slate-600">
            {(showCommon ? commonContemporaries : anyContemporaries).slice(0, 30).map((p) => (
              <li key={p.id} className="flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: genderColor(p.gender) }}
                />
                <span className="truncate">{personLabel(p)}</span>
                <span className="ml-auto flex-shrink-0 text-slate-400">{lifespan(p)}</span>
              </li>
            ))}
            {(showCommon ? commonContemporaries : anyContemporaries).length > 30 && (
              <li className="text-slate-400">
                … und {(showCommon ? commonContemporaries : anyContemporaries).length - 30} weitere
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── LifelinePage ─────────────────────────────────────────────────────────────

export function LifelinePage() {
  const currentYear = new Date().getFullYear();

  // ── Daten laden ───────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery<LifelinesData>({
    queryKey: ['lifelines'],
    queryFn: fetchLifelines,
    staleTime: 60_000,
  });

  // ── Zustand ───────────────────────────────────────────────────────────────
  const [pxPerYear, setPxPerYear]       = useState(10);
  const [showOverlays, setShowOverlays] = useState(true);
  const [selectedIds, setSelectedIds]   = useState<Set<string>>(new Set());
  const [hoveredId, setHoveredId]       = useState<string | null>(null);
  const [tooltip, setTooltip]           = useState<TooltipData | null>(null);
  const [search, setSearch]             = useState('');

  // ── Scroll-Synchronisation ────────────────────────────────────────────────
  const axisScrollRef   = useRef<HTMLDivElement>(null);
  const timelineBodyRef = useRef<HTMLDivElement>(null);
  const nameColRef      = useRef<HTMLDivElement>(null);

  const handleTimelineScroll = useCallback(() => {
    if (!timelineBodyRef.current) return;
    const { scrollLeft, scrollTop } = timelineBodyRef.current;
    if (axisScrollRef.current)  axisScrollRef.current.scrollLeft  = scrollLeft;
    if (nameColRef.current)     nameColRef.current.scrollTop       = scrollTop;
  }, []);

  // ── Berechnete Werte ──────────────────────────────────────────────────────
  const persons = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.persons;
    return data.persons.filter((p) =>
      personLabel(p).toLowerCase().includes(q),
    );
  }, [data, search]);

  const selectedPersons = useMemo(
    () => persons.filter((p) => selectedIds.has(p.id)),
    [persons, selectedIds],
  );

  const totalW = useMemo(() => {
    if (!data) return 0;
    return (data.yearMax - data.yearMin) * pxPerYear;
  }, [data, pxPerYear]);

  // ── Interaktion ───────────────────────────────────────────────────────────
  const handleToggle = useCallback((p: LifelinePerson) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) {
        next.delete(p.id);
      } else {
        next.add(p.id);
      }
      return next;
    });
  }, []);

  const handleRemove = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => setSelectedIds(new Set()), []);

  const handleMilestoneHover = useCallback(
    (e: React.MouseEvent<SVGElement>, lines: string[]) => {
      const rect = (e.currentTarget as SVGElement)
        .closest('svg')!
        .getBoundingClientRect();
      setTooltip({ x: e.clientX - rect.left + 8, y: e.clientY - rect.top - 12, lines });
    },
    [],
  );

  const handleMilestoneLeave = useCallback(() => setTooltip(null), []);

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size="lg" message="Lebenslinien werden geladen…" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !data) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-red-500">Fehler beim Laden der Lebenslinien.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-full flex-col overflow-hidden bg-slate-50">

        {/* ── Toolbar ────────────────────────────────────────────────────── */}
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:gap-3 sm:px-4">
          {/* Titel */}
          <div className="mr-2">
            <h1 className="text-sm font-bold text-slate-800">Lebenslinien</h1>
            <p className="text-[10px] text-slate-400">{persons.length} Personen · {data.yearMin}–{data.yearMax}</p>
          </div>

          {/* Suche */}
          <div className="relative">
            <svg className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Person filtern…"
              className="w-28 rounded-md border border-slate-200 bg-slate-50 pl-7 pr-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 sm:w-44"
            />
          </div>

          <div className="h-5 w-px bg-slate-200" />

          {/* Overlays-Toggle */}
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showOverlays}
              onChange={(e) => setShowOverlays(e.target.checked)}
              className="h-3.5 w-3.5 rounded accent-blue-500"
            />
            Historische Epochen
          </label>

          <div className="h-5 w-px bg-slate-200" />

          {/* Zoom */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500">Zoom</span>
            <button
              onClick={() => setPxPerYear((v) => Math.max(2, v - 2))}
              disabled={pxPerYear <= 2}
              className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30"
            >−</button>
            <span className="w-8 text-center text-xs font-mono text-slate-600">{pxPerYear}px</span>
            <button
              onClick={() => setPxPerYear((v) => Math.min(40, v + 2))}
              disabled={pxPerYear >= 40}
              className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30"
            >+</button>
          </div>

          {/* Auswahl aufheben */}
          {selectedIds.size > 0 && (
            <>
              <div className="h-5 w-px bg-slate-200" />
              <button
                onClick={handleClearAll}
                className="flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
                {selectedIds.size === 1 ? 'Filter aufheben' : `${selectedIds.size} Auswahlen aufheben`}
              </button>
            </>
          )}

          {/* Legende */}
          <div className="ml-auto hidden items-center gap-3 text-[10px] text-slate-500 sm:flex">
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded-sm bg-blue-400 opacity-70" />Männlich</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded-sm bg-pink-400 opacity-70" />Weiblich</span>
            <span className="flex items-center gap-1">
              <svg className="h-3 w-3" viewBox="0 0 12 12"><circle cx="6" cy="6" r="4" fill="none" stroke="#3b82f6" strokeWidth="1.5"/><circle cx="6" cy="6" r="2" fill="#3b82f6" opacity="0.6"/></svg>
              Hochzeit
            </span>
            <span className="flex items-center gap-1">
              <svg className="h-3 w-3" viewBox="0 0 12 12"><circle cx="6" cy="6" r="3.5" fill="#f59e0b"/></svg>
              Geburt (Kind)
            </span>
          </div>
        </div>

        {/* ── Diagramm-Body ──────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 overflow-hidden">

          {/* Linke Namensspalte */}
          <div
            className="flex flex-shrink-0 flex-col border-r border-slate-200 bg-white"
            style={{ width: `clamp(120px, 45vw, ${NAME_COL_W}px)` }}
          >
            {/* Kopfzeile über der Achse */}
            <div
              className="flex flex-shrink-0 items-end border-b border-slate-200 bg-slate-50 px-2 pb-1.5"
              style={{ height: AXIS_H }}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Person
              </span>
            </div>
            {/* Namen – scrollt vertikal synchron mit dem Timeline-Body */}
            <div
              ref={nameColRef}
              className="flex-1"
              style={{
                overflowY: 'scroll',
                /* Scrollbar ausblenden – sichtbar nur im Timeline-Body */
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              } as React.CSSProperties}
            >
              <NameColumn
                persons={persons}
                selectedIds={selectedIds}
                hoveredId={hoveredId}
                selectedPersons={selectedPersons}
                currentYear={currentYear}
                onToggle={handleToggle}
                onHover={setHoveredId}
              />
            </div>
          </div>

          {/* Rechter Bereich: Achse + Timeline */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Jahres-Achse (horizontal gescrollt, aber sticky oben) */}
            <div
              ref={axisScrollRef}
              className="flex-shrink-0 overflow-hidden border-b border-slate-200 bg-white"
              style={{ height: AXIS_H, overflowX: 'hidden' }}
            >
              <YearAxis
                yearMin={data.yearMin}
                yearMax={data.yearMax}
                pxPerYear={pxPerYear}
                width={totalW}
                overlays={OVERLAYS}
                showOverlays={showOverlays}
              />
            </div>

            {/* Timeline-Körper: horizontal + vertikal scrollbar */}
            <div
              ref={timelineBodyRef}
              className="flex-1 overflow-auto"
              onScroll={handleTimelineScroll}
            >
              {/* Relativer Container für Tooltip-Positionierung */}
              <div style={{ position: 'relative', width: totalW, minHeight: persons.length * ROW_H }}>
                <TimelineSVG
                  persons={persons}
                  yearMin={data.yearMin}
                  yearMax={data.yearMax}
                  pxPerYear={pxPerYear}
                  showOverlays={showOverlays}
                  selectedIds={selectedIds}
                  hoveredId={hoveredId}
                  currentYear={currentYear}
                  onPersonClick={handleToggle}
                  onMouseMove={handleMilestoneHover}
                  onMouseLeave={handleMilestoneLeave}
                />

                {/* Tooltip */}
                {tooltip && (
                  <div
                    style={{
                      position: 'absolute',
                      top: tooltip.y,
                      left: tooltip.x,
                      pointerEvents: 'none',
                      zIndex: 50,
                    }}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1.5 shadow-lg"
                  >
                    {tooltip.lines.map((l, i) => (
                      <p key={i} className={`text-xs ${i === 0 ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>{l}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Zeitzeugen-Sidebar */}
          {selectedPersons.length > 0 && (
            <ZeitzeugenInfo
              selectedPersons={selectedPersons}
              allPersons={persons}
              currentYear={currentYear}
              onClose={handleClearAll}
              onRemove={handleRemove}
            />
          )}
        </div>

        {/* ── Epochen-Legende (unten) ────────────────────────────────────── */}
        {showOverlays && (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-200 bg-white px-4 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Epochen:</span>
            {OVERLAYS.map((o) => (
              <span key={o.label} className="flex items-center gap-1 text-[10px] text-slate-500">
                <span
                  className="inline-block h-2.5 w-3 rounded-sm"
                  style={{ backgroundColor: o.color, opacity: o.opacity * 5 + 0.3 }}
                />
                {o.label} ({o.start}–{o.end})
              </span>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
